import { useQuery } from "@tanstack/react-query"

import type { ChangeEvent } from "@/lib/changeEvents"
import { supabase } from "@/lib/supabase"

/**
 * Reads of the route change history.
 *
 * Straight from PostgREST rather than through the collections edge function, for the same reason
 * `useRouteExports` is: the table's policy admits admins and operators only, and that depends on
 * the caller's own JWT, so the check belongs where the database enforces it. Somebody without a
 * collections role gets an empty result whatever the UI renders.
 */

const KEY = ["collections-change-events"]

/** Every column the panels read. Named rather than `*` so a schema addition cannot surprise them. */
const COLUMNS =
  "id, occurred_at, correlation_id, sequence_in_action, parent_event_id, event_type, scope, " +
  "cause, status, error, actor_id, actor_email, actor_name, actor_kind, actor_verified, " +
  "planned_route_id, planned_route_code, route_instance_id, route_instance_code, " +
  "route_instance_date, entity_table, entity_id, entity_label, before, after, " +
  "is_full_snapshot, changed_fields, summary, impact_from, impact_to, impacted_dates, " +
  "impacted_instance_count"

export function instanceChangeEventsQueryKey(routeInstanceIds: string[]) {
  return [...KEY, "instances", [...routeInstanceIds].sort().join(",")]
}

/**
 * History for a set of route instances.
 *
 * Takes a set rather than one id so the monitoring timeline can load a whole date in one request,
 * rather than one request per route on every background refetch.
 */
export function useInstanceChangeEvents(routeInstanceIds: string[]) {
  return useQuery({
    queryKey: instanceChangeEventsQueryKey(routeInstanceIds),
    enabled: routeInstanceIds.length > 0,
    queryFn: async (): Promise<ChangeEvent[]> => {
      const { data, error } = await supabase
        .from("collections_change_events")
        .select(COLUMNS)
        .in("route_instance_id", routeInstanceIds)
        .order("occurred_at", { ascending: false })
        .order("sequence_in_action", { ascending: true })

      if (error) throw new Error(error.message)
      return (data ?? []) as unknown as ChangeEvent[]
    },
  })
}

export function planLineageQueryKey(plannedRouteId: string | undefined) {
  return [...KEY, "plan", plannedRouteId ?? "none"]
}

/**
 * One plan's whole story: its own events and the instance events beneath them.
 *
 * This is the lineage view, and it works because every instance-scope row carries
 * `planned_route_id` — including the ones nobody caused from the template. Without that, this
 * query would return the changes that came down from the plan and miss the ones somebody made to
 * one Tuesday directly, which is the more common kind.
 */
export function usePlanLineage(plannedRouteId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: planLineageQueryKey(plannedRouteId),
    enabled: !!plannedRouteId && enabled,
    queryFn: async (): Promise<ChangeEvent[]> => {
      const { data, error } = await supabase
        .from("collections_change_events")
        .select(COLUMNS)
        .eq("planned_route_id", plannedRouteId!)
        .order("occurred_at", { ascending: false })
        .order("sequence_in_action", { ascending: true })
        .limit(500)

      if (error) throw new Error(error.message)
      return (data ?? []) as unknown as ChangeEvent[]
    },
  })
}
